/* eslint-disable @typescript-eslint/camelcase */
import {
  UserEntities,
  UserData,
  UserDetailData,
  ReferenceEntities,
  RoleEntities,
  QuotaTicketAccount,
  DivisionEntities,
  ShuttleEntities
} from "@/domain/entities/User";
import { Pagination } from "@/domain/entities/Pagination";
import { AxiosResponse } from "axios";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { Hub } from "@/domain/entities/Hub";

export class UserDataMapper {
  // Helper to map basic user account fields
  private mapBasicUserFields(item: any): Partial<UserData> {
    return {
      account_id: item.account_id,
      username: item.username,
      account_name: item.account_name,
      account_email: item.account_email,
      password: item.password,
      account_phone_number: item.account_phone_number,
      status: item.status,
      created_at: item.created_at,
      created_by: item.created_by,
      updated_at: item.updated_at,
      updated_by: item.updated_by,
      first_login: item.first_login,
      account_type: item.account_type,
      role_id: item.role_id
    };
  }

  // Helper to map account role and related fields
  private mapAccountRoleFields(item: any): Partial<UserData> {
    return {
      account_role: item.account_role.name,
      account_partner: item.account_partner?.partner,
      account_client: item.account_client?.client,
      account_vendor: item.account_vendor?.vendor,
      division_name: item.account_role?.division?.division_name
    };
  }

  // Helper to map hub and shuttle fields
  private mapHubShuttleFields(item: any): Partial<UserData> {
    return {
      hub_name: item.hub?.hub_name,
      shuttle_name: item.shuttle?.shuttle_name
    };
  }

  // Helper to map quota ticket account fields
  private mapQuotaTicketAccountFields(item: any): Partial<UserData> {
    const dexBucket = item.dex_bucket_ticket_account;
    return {
      dexBucketTicketAccount: new QuotaTicketAccount({
        dbtaId: dexBucket?.dbta_id,
        dbtaBucketTicket: String(dexBucket?.dbta_bucket_ticket),
        dbtaDexReasonCode: dexBucket?.dbta_dex_reason_code ?? null,
        dbtaDisputeDexReasonCode:
          dexBucket?.dbta_dispute_dex_reason_code ?? null,
        dbtaThreeLc: dexBucket?.dbta_three_lc || []
      }),
      dbtaThreeLc: dexBucket?.dbta_three_lc || []
    };
  }

  // Helper to map single user item
  private mapUserItem(item: any): UserData {
    return new UserData({
      ...this.mapBasicUserFields(item),
      ...this.mapAccountRoleFields(item),
      ...this.mapHubShuttleFields(item),
      ...this.mapQuotaTicketAccountFields(item)
    });
  }

  public convertUserDataFromApi(result: AxiosResponse<any>): UserEntities {
    const { data } = result;
    const dataList = data.data || [];
    const userListData = dataList.map((item: any) => this.mapUserItem(item));

    const pagination = new Pagination(
      data.meta.page,
      data.meta.limit,
      data.meta.total_records
    );

    return new UserEntities(pagination, userListData);
  }

  public convertUserV4DataFromApi(result: AxiosResponse<any>): UserEntities {
    const { data } = result;
    const dataList = data.data || [];
    const userListData = dataList.map((item: any) => this.mapBasicUserFields(item));

    const pagination = new Pagination(
      data.meta.page,
      data.meta.limit,
      data.meta.total_records
    );

    return new UserEntities(pagination, userListData);
  }

  public convertDetailUserDataFromApi(
    result: AxiosResponse<any>
  ): UserDetailData {
    const { data } = result;

    return new UserDetailData({
      id: data.data.id,
      username: data.data.username,
      name: data.data.name,
      email: data.data.email,
      phone_number: data.data.phone_number,
      status: data.data.status,
      created_at: data.data.created_at,
      created_by: data.data.created_by,
      updated_at: data.data.updated_at,
      updated_by: data.data.updated_by,
      account_type: data.data.account_type,
      account_hub_code: data.data.account_hub_code,
      role_name: data.data.role_name,
      partner: data.data?.partner,
      client: data.data?.client,
      vendor: data.data?.vendor,
      assigned3lc: data?.data?.meta?.assigned_3lc,
      hub: this.mapHubData(data),
      division_name: data.data.division_name,
      division_id: data.data.division_id,
      shuttle_name: data.data.shuttle_name,
      shuttle_id: data.data.shuttle_id
    });
  }

  public mapHubData(data: any) {
    if (!data.data.hub_detail) return new Hub();
    return new Hub({
      hubId: data.data.hub_detail.hub_id,
      hubName: data.data.hub_detail.hub_name,
      hubCode: data.data.hub_detail.hub_code,
      originCity: data.data.hub_detail.origin_city,
      status: data.data.hub_detail.status,
      hubDistrictCode: data.data.hub_detail.hub_district_code
    });
  }

  public convertChangeDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertReferenceDataFromApi(
    result: AxiosResponse<any>
  ): ReferenceEntities[] {
    const { data } = result;

    const refenceList = data.data || [];

    return refenceList.map((item: any) => {
      return new ReferenceEntities({
        id: item.id,
        names: item?.name,
        companyName: item?.company_name,
        status: item?.status,
        partnerCityCode: item?.partner_location?.city_code || ""
      });
    });
  }

  public convertDivisionDataFromApi(
    result: AxiosResponse<any>
  ): DivisionEntities[] {
    const { data } = result;

    const divisionList = data.data || [];

    return divisionList.map((item: any) => {
      return new DivisionEntities({
        divisionId: item.division_id,
        divisionName: item.division_name,
        departmentId: item.department_id,
        departmentName: item.department_name
      });
    });
  }
  public convertRoleDataFromApi(result: AxiosResponse<any>): RoleEntities[] {
    const { data } = result;

    const roleList = data.data || [];

    return roleList.map((item: any) => {
      return new RoleEntities(item.id, item.name);
    });
  }

  public convertShuttleDataFromApi(
    result: AxiosResponse<any>
  ): ShuttleEntities[] {
    const { data } = result;

    const shuttleList = data.data || [];

    return shuttleList.map((item: any) => {
      return new ShuttleEntities({
        shuttleId: item.shuttle_id,
        shuttlePartnerId: item.shuttle_partner_id,
        shuttleCode: item.shuttle_code,
        shuttleName: item.shuttle_name,
        shuttleHubId: item.shuttle_hub_id
      });
    });
  }

  public convertFormDataFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const {
      data: { data }
    } = result;

    return new ResponsePayloadV2({ data });
  }
}
